import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, map, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  StatsFilterService,
  StatsFilters,
  ComparisonPeriod,
} from './statistics-filter.service';
import { StatisticsService } from '../../_services/statistics/statistics.service';
import ITreatmentsRanking from '../../_interfaces/responses/ITreatmentsRanking';
import moment from 'moment';

// Extended filters type with comparison period
interface StatsFiltersWithComparison extends StatsFilters {
  comparisonPeriod: ComparisonPeriod | null;
}

// ============================================================================
// INTERFACES
// ============================================================================

export interface KpiValue {
  value: number;
  previousValue: number | null;
  trend: number | null;
}

export interface DashboardKpis {
  revenue: KpiValue;
  appointments: KpiValue;
  averageTicket: KpiValue;
  newPatients: KpiValue;
  cancellationRate: KpiValue;
  occupancyRate: KpiValue;
}

export interface RevenueSummary {
  totalRevenue: KpiValue;
  transactions: KpiValue;
  averageTicket: KpiValue;
}

export interface TimeSeriesPoint {
  date: string;
  value: number;
  label?: string;
}

export interface CategoryData {
  id: number;
  name: string;
  value: number;
  count: number;
  percentage?: number;
  color?: string;
}

export interface TopItem {
  id: number;
  name: string;
  value: number;
  count: number;
  secondaryCount?: number; // Para mostrar info adicional (ej: citas cuando count son tratamientos)
  trend?: number;
  routerLink?: string | unknown[];
}

export interface RevenueByTreatment {
  treatmentId: number;
  treatmentName: string;
  categoryName: string;
  revenue: number;
  count: number;
  averagePrice: number;
  trend?: number;
  previousRevenue?: number;
}

export interface PatientMetrics {
  totalPatients: number;
  newPatients: number;
  returningPatients: number;
  retentionRate: number;
  churnRate: number;
  avgVisitsPerPatient: number;
  ltv: number;
  totalPatientsKpi?: KpiValue;
  newPatientsKpi?: KpiValue;
  returningPatientsKpi?: KpiValue;
  retentionRateKpi?: KpiValue;
  churnRateKpi?: KpiValue;
  avgVisitsPerPatientKpi?: KpiValue;
  ltvKpi?: KpiValue;
}

export interface AppointmentMetrics {
  totalAppointments: number;
  completedAppointments: number;
  completed: number;
  cancelled: number;
  noShows: number;
  noShowRate: number;
  cancellationRate: number;
  occupancyRate: number;
  avgDuration: number;
  totalAppointmentsKpi?: KpiValue;
  completedAppointmentsKpi?: KpiValue;
  noShowRateKpi?: KpiValue;
  cancellationRateKpi?: KpiValue;
  occupancyRateKpi?: KpiValue;
  avgDurationKpi?: KpiValue;
}

export interface AppointmentsHeatmapData {
  days: Array<{
    dayNumber: number;
    dayName: string;
  }>;
  hours: number[];
  matrix: number[][];
  peakSlot: {
    dayNumber: number;
    dayName: string;
    hour: number;
    count: number;
  };
  total: number;
}

export interface CohortData {
  cohort: string;
  cohortMonth?: string;
  initialPatients?: number;
  month0?: number;
  month1: number;
  month2: number;
  month3: number;
  month4?: number;
  month5?: number;
  month6: number;
  month12: number;
}

export interface HourlyDistribution {
  hour: number;
  count: number;
  revenue: number;
}

export interface HourlyData {
  hour: number;
  count: number;
}

export interface WeeklyDistribution {
  day: number;
  dayName: string;
  count: number;
  revenue: number;
}

// ============================================================================
// SERVICE - NO FAKE DATA, ERRORS WILL PROPAGATE
// ============================================================================

@Injectable({
  providedIn: 'root',
})
export class StatsDataService {
  private apiUrl = `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1`;

  constructor(
    private http: HttpClient,
    private filterService: StatsFilterService,
    private statisticsService: StatisticsService,
  ) {}

  // ============================================================================
  // DASHBOARD
  // ============================================================================

  /**
   * GET /statistics/dashboard
   */
  getDashboardKpis(
    customerId: number,
    siteId: number,
  ): Observable<DashboardKpis> {
    const filters = this.filterService.getCurrentFilters();
    const params = this.buildDateParams(filters);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/dashboard`;

    console.log('📊 [STATS] getDashboardKpis:', url);

    return this.http.get<any>(url, { params }).pipe(
      tap((res) => console.log('✅ [STATS] getDashboardKpis response:', res)),
      map((response) => {
        const data = response?.success ? response.data : response;
        return data as DashboardKpis;
      }),
    );
  }

  /**
   * GET /statistics/revenue/summary
   * KPIs específicos de la sección Revenue
   */
  getRevenueSummary(
    customerId: number,
    siteId: number,
  ): Observable<RevenueSummary> {
    const filters = this.filterService.getCurrentFilters();
    const params = this.buildDateParams(filters);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/revenue/summary`;

    console.log('📊 [STATS] getRevenueSummary:', url);

    return this.http.get<any>(url, { params }).pipe(
      tap((res) => console.log('✅ [STATS] getRevenueSummary response:', res)),
      map((response) => {
        const data = response?.success ? response.data : response;
        return data as RevenueSummary;
      }),
    );
  }

  /**
   * GET /statistics/revenue/timeline
   */
  getRevenueTimeline(
    customerId: number,
    siteId: number,
    granularity: 'day' | 'week' | 'month' | 'quarter' | 'year' = 'day',
  ): Observable<TimeSeriesPoint[]> {
    const filters = this.filterService.getCurrentFilters();
    let params = this.buildDateParams(filters);
    params = params.set('granularity', granularity);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/revenue/timeline`;

    console.log('📊 [STATS] getRevenueTimeline:', url);

    return this.http.get<any>(url, { params }).pipe(
      tap((res) => console.log('✅ [STATS] getRevenueTimeline response:', res)),
      map((response) => {
        const data = response?.success ? response.data : response;
        return (data?.series || []) as TimeSeriesPoint[];
      }),
    );
  }

  /**
   * Uses existing endpoint: treatments_ranking
   * Now with date filtering support
   */
  getTopTreatments(
    customerId: number,
    siteId: number,
    limit: number = 5,
  ): Observable<TopItem[]> {
    const filters = this.filterService.getCurrentFilters();
    const startDate = filters.dateRange?.start
      ? moment(filters.dateRange.start).format('YYYY-MM-DD')
      : undefined;
    const endDate = filters.dateRange?.end
      ? moment(filters.dateRange.end).format('YYYY-MM-DD')
      : undefined;

    console.log('📊 [STATS] getTopTreatments:', { startDate, endDate });

    return this.statisticsService
      .treatments_ranking(customerId, siteId, startDate, endDate)
      .pipe(
        tap((res) => console.log('✅ [STATS] getTopTreatments response:', res)),
        map((response: ITreatmentsRanking) => {
          if (!response?.treatments) return [];
          return response.treatments
            .slice(0, limit)
            .map((t: any, index: number) => ({
              id: index + 1,
              name: t.t_name,
              value: parseFloat(t.revenue || '0'),
              count: parseInt(t.count, 10) || 0,
            }));
        }),
      );
  }

  /**
   * Uses existing endpoint: patients_count_appointments
   * Now with date filtering support
   */
  getTopPatients(
    customerId: number,
    siteId: number,
    limit: number = 5,
  ): Observable<TopItem[]> {
    const filters = this.filterService.getCurrentFilters();
    const startDate = filters.dateRange?.start
      ? moment(filters.dateRange.start).format('YYYY-MM-DD')
      : undefined;
    const endDate = filters.dateRange?.end
      ? moment(filters.dateRange.end).format('YYYY-MM-DD')
      : undefined;

    console.log('📊 [STATS] getTopPatients:', { startDate, endDate });

    return this.statisticsService
      .patients_count_appointments(customerId, siteId, startDate, endDate)
      .pipe(
        tap((res) => console.log('✅ [STATS] getTopPatients response:', res)),
        map((data: any) => {
          if (!Array.isArray(data)) return [];
          return data.slice(0, limit).map((p: any) => ({
            id: p.id || p.patient_id || p.patients_id,
            name: `${p.name || ''} ${p.surname || ''}`.trim(),
            value: parseFloat(p.totalRevenue || '0'),
            count: parseInt(p.treatmentCount, 10) || 0,
            secondaryCount: parseInt(p.appointmentCount, 10) || 0,
          }));
        }),
      );
  }

  /**
   * GET /statistics/revenue/by-payment-method
   */
  getPaymentMethodDistribution(
    customerId: number,
    siteId: number,
  ): Observable<CategoryData[]> {
    const filters = this.filterService.getCurrentFilters();
    const params = this.buildDateParams(filters);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/revenue/by-payment-method`;

    console.log('📊 [STATS] getPaymentMethodDistribution:', url);

    return this.http.get<any>(url, { params }).pipe(
      tap((res) =>
        console.log('✅ [STATS] getPaymentMethodDistribution response:', res),
      ),
      map((response) => {
        const data = response?.success ? response.data : response;
        if (!data?.methods) return [];
        return data.methods.map((m: any) => ({
          id: m.method || m.paymentMethodId,
          name: m.name,
          value: m.revenue,
          count: m.count,
          percentage: m.percentage,
          color: m.color,
        }));
      }),
    );
  }

  // ============================================================================
  // REVENUE
  // ============================================================================

  /**
   * Uses existing endpoint: treatments_ranking
   * Now with date filtering support and trend calculation
   * Makes 2 API calls: current period + previous period to calculate trends
   */
  getRevenueByTreatment(
    customerId: number,
    siteId: number,
  ): Observable<RevenueByTreatment[]> {
    const filters = this.filterService.getCurrentFilters();
    const startDate = filters.dateRange?.start
      ? moment(filters.dateRange.start).format('YYYY-MM-DD')
      : undefined;
    const endDate = filters.dateRange?.end
      ? moment(filters.dateRange.end).format('YYYY-MM-DD')
      : undefined;

    // Calcular período anterior para tendencias
    const comparisonPeriod = filters.comparisonPeriod;
    const prevStartDate = comparisonPeriod?.start
      ? moment(comparisonPeriod.start).format('YYYY-MM-DD')
      : undefined;
    const prevEndDate = comparisonPeriod?.end
      ? moment(comparisonPeriod.end).format('YYYY-MM-DD')
      : undefined;

    console.log('📊 [STATS] getRevenueByTreatment:', {
      current: { startDate, endDate },
      previous: { prevStartDate, prevEndDate },
    });

    // Llamada al período actual
    const currentPeriod$ = this.statisticsService
      .treatments_ranking(customerId, siteId, startDate, endDate)
      .pipe(
        tap((res) =>
          console.log('✅ [STATS] getRevenueByTreatment current:', res),
        ),
      );

    // Si no hay período de comparación, solo devolver datos actuales sin tendencia
    if (!prevStartDate || !prevEndDate) {
      return currentPeriod$.pipe(
        map((response: ITreatmentsRanking) =>
          this.mapTreatmentsResponse(response, null),
        ),
      );
    }

    // Llamada al período anterior para calcular tendencias
    const previousPeriod$ = this.statisticsService
      .treatments_ranking(customerId, siteId, prevStartDate, prevEndDate)
      .pipe(
        tap((res) =>
          console.log('✅ [STATS] getRevenueByTreatment previous:', res),
        ),
      );

    // Combinar ambas respuestas y calcular tendencias
    return forkJoin([currentPeriod$, previousPeriod$]).pipe(
      map(([current, previous]) =>
        this.mapTreatmentsResponse(current, previous),
      ),
    );
  }

  /**
   * Helper para mapear la respuesta de treatments_ranking
   */
  private mapTreatmentsResponse(
    current: ITreatmentsRanking,
    previous: ITreatmentsRanking | null,
  ): RevenueByTreatment[] {
    if (!current?.treatments) return [];

    // Crear mapa del período anterior por nombre de tratamiento
    const previousMap = new Map<string, number>();
    if (previous?.treatments) {
      previous.treatments.forEach((t: any) => {
        const revenue =
          t.revenue !== undefined
            ? parseFloat(t.revenue) || 0
            : this.extractRevenue(t.currencies);
        previousMap.set(t.t_name, revenue);
      });
    }

    return current.treatments.map((t: any, index: number) => {
      const revenue =
        t.revenue !== undefined
          ? parseFloat(t.revenue) || 0
          : this.extractRevenue(t.currencies);
      const count = parseInt(t.count, 10) || 1;

      // Calcular tendencia si hay datos del período anterior
      let trend: number | undefined = undefined;
      const prevRevenue = previousMap.get(t.t_name);
      if (prevRevenue !== undefined && prevRevenue > 0) {
        trend = (revenue - prevRevenue) / prevRevenue;
      } else if (prevRevenue === 0 && revenue > 0) {
        trend = 1; // 100% de incremento (de 0 a algo)
      }

      return {
        treatmentId: index + 1,
        treatmentName: t.t_name,
        categoryName: t.c_name || 'Sin categoría',
        revenue,
        count,
        averagePrice: count > 0 ? revenue / count : 0,
        trend,
        previousRevenue: prevRevenue,
      };
    });
  }

  /**
   * GET /statistics/revenue/by-category
   */
  getRevenueByCategory(
    customerId: number,
    siteId: number,
  ): Observable<CategoryData[]> {
    const filters = this.filterService.getCurrentFilters();
    const params = this.buildDateParams(filters);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/revenue/by-category`;

    console.log('📊 [STATS] getRevenueByCategory:', url);

    return this.http.get<any>(url, { params }).pipe(
      tap((res) =>
        console.log('✅ [STATS] getRevenueByCategory response:', res),
      ),
      map((response) => {
        const data = response?.success ? response.data : response;
        if (!data?.categories) return [];
        return data.categories.map((c: any) => ({
          id: c.id || c.categoryId,
          name: c.name,
          value: c.revenue,
          count: c.count,
          percentage: c.percentage,
          color: c.color,
        }));
      }),
    );
  }

  // ============================================================================
  // PATIENTS
  // ============================================================================

  /**
   * GET /statistics/patients/metrics
   */
  getPatientMetrics(
    customerId: number,
    siteId: number,
  ): Observable<PatientMetrics> {
    const filters = this.filterService.getCurrentFilters();
    const params = this.buildDateParams(filters);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/patients/metrics`;

    console.log('📊 [STATS] getPatientMetrics:', url);

    return this.http.get<any>(url, { params }).pipe(
      tap((res) => console.log('✅ [STATS] getPatientMetrics response:', res)),
      map((response) => {
        const data = response?.success ? response.data : response;
        const totalPatientsKpi = this.toKpiValue(data.totalActive);
        const newPatientsKpi = this.toKpiValue(data.newPatients);
        const returningPatientsKpi = this.toKpiValue(data.returningPatients);
        const retentionRateKpi = this.toKpiValue(data.retentionRate);
        const churnRateKpi = this.toKpiValue(data.churnRate);
        const avgVisitsKpi = this.toKpiValue(data.avgVisitsPerPatient);
        const ltvKpi = this.toKpiValue(data.avgLifetimeValue ?? data.ltv);

        return {
          totalPatients: totalPatientsKpi.value,
          newPatients: newPatientsKpi.value,
          returningPatients: returningPatientsKpi.value,
          retentionRate: retentionRateKpi.value,
          churnRate: churnRateKpi.value,
          avgVisitsPerPatient: avgVisitsKpi.value,
          ltv: ltvKpi.value,
          totalPatientsKpi,
          newPatientsKpi,
          returningPatientsKpi,
          retentionRateKpi,
          churnRateKpi,
          avgVisitsPerPatientKpi: avgVisitsKpi,
          ltvKpi,
        };
      }),
    );
  }

  /**
   * Uses existing endpoint: patients_acquired_weekly
   */
  getPatientGrowth(
    customerId: number,
    siteId: number,
  ): Observable<TimeSeriesPoint[]> {
    const filters = this.filterService.getCurrentFilters();
    const granularity =
      filters.granularity === 'day' ||
      filters.granularity === 'week' ||
      filters.granularity === 'month'
        ? filters.granularity
        : 'week';
    let params = this.buildDateParams(filters);
    params = params.set('granularity', granularity);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/patients/growth-timeline`;

    console.log('📊 [STATS] getPatientGrowth:', { url, granularity });

    return this.http.get<any>(url, { params }).pipe(
      tap((res) => console.log('✅ [STATS] getPatientGrowth response:', res)),
      map((response) => {
        const data = response?.success ? response.data : response;
        return (data?.series || []) as TimeSeriesPoint[];
      }),
    );
  }

  /**
   * GET /statistics/patients/cohort-retention
   */
  getCohortRetention(
    customerId: number,
    siteId: number,
  ): Observable<CohortData[]> {
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/patients/cohort-retention`;

    console.log('📊 [STATS] getCohortRetention:', url);

    return this.http
      .get<any>(url, { params: new HttpParams().set('months', '6') })
      .pipe(
        tap((res) =>
          console.log('✅ [STATS] getCohortRetention response:', res),
        ),
        map((response) => {
          const data = response?.success ? response.data : response;
          if (!data?.cohorts) return [];
          return data.cohorts.map((c: any) => ({
            cohort: c.cohortLabel || c.cohort,
            cohortMonth: c.cohortMonth,
            initialPatients: c.initialPatients,
            month0: c.retention?.month0 ?? 100,
            month1: c.retention?.month1 ?? c.month1 ?? 0,
            month2: c.retention?.month2 ?? c.month2 ?? 0,
            month3: c.retention?.month3 ?? c.month3 ?? 0,
            month4: c.retention?.month4 ?? c.month4 ?? 0,
            month5: c.retention?.month5 ?? c.month5 ?? 0,
            month6: c.retention?.month6 ?? c.month6 ?? 0,
            month12: c.retention?.month12 ?? c.month12 ?? 0,
          }));
        }),
      );
  }

  /**
   * GET /statistics/patients/age-distribution
   */
  getPatientAgeDistribution(
    customerId: number,
    siteId: number,
  ): Observable<CategoryData[]> {
    const filters = this.filterService.getCurrentFilters();
    const params = this.buildDateParams(filters);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/patients/age-distribution`;

    console.log('📊 [STATS] getPatientAgeDistribution:', url);

    return this.http.get<any>(url, { params }).pipe(
      tap((res) =>
        console.log('✅ [STATS] getPatientAgeDistribution response:', res),
      ),
      map((response) => {
        const data = response?.success ? response.data : response;
        if (!data?.distribution) return [];
        return data.distribution.map((d: any, index: number) => ({
          id: index + 1,
          name: d.ageRange,
          value: d.revenue || 0,
          count: d.patientCount,
          percentage: d.percentage,
        }));
      }),
    );
  }

  // ============================================================================
  // APPOINTMENTS
  // ============================================================================

  /**
   * GET /statistics/appointments/metrics
   */
  getAppointmentMetrics(
    customerId: number,
    siteId: number,
  ): Observable<AppointmentMetrics> {
    const filters = this.filterService.getCurrentFilters();
    const params = this.buildDateParams(filters);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/appointments/metrics`;

    console.log('📊 [STATS] getAppointmentMetrics:', url);

    return this.http.get<any>(url, { params }).pipe(
      tap((res) =>
        console.log('✅ [STATS] getAppointmentMetrics response:', res),
      ),
      map((response) => {
        const data = response?.success ? response.data : response;
        const totalAppointmentsKpi = this.toKpiValue(data.total);
        const completedAppointmentsKpi = this.toKpiValue(data.completed);
        const noShowRateKpi = this.toKpiValue(data.noShowRate);
        const cancellationRateKpi = this.toKpiValue(data.cancellationRate);
        const occupancyRateKpi = this.toKpiValue(data.occupancyRate);
        const avgDurationKpi = this.toKpiValue(data.avgDuration);

        return {
          totalAppointments: totalAppointmentsKpi.value,
          completedAppointments: completedAppointmentsKpi.value,
          completed: data.completed?.value ?? data.completed ?? 0,
          cancelled: data.cancelled?.value ?? data.cancelled ?? 0,
          noShows: data.noShows?.value ?? data.noShows ?? 0,
          noShowRate: noShowRateKpi.value,
          cancellationRate: cancellationRateKpi.value,
          occupancyRate: occupancyRateKpi.value,
          avgDuration: avgDurationKpi.value,
          totalAppointmentsKpi,
          completedAppointmentsKpi,
          noShowRateKpi,
          cancellationRateKpi,
          occupancyRateKpi,
          avgDurationKpi,
        };
      }),
    );
  }

  /**
   * GET /statistics/appointments/by-hour
   */
  getHourlyDistribution(
    customerId: number,
    siteId: number,
  ): Observable<HourlyDistribution[]> {
    const filters = this.filterService.getCurrentFilters();
    const params = this.buildDateParams(filters);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/appointments/by-hour`;

    console.log('📊 [STATS] getHourlyDistribution:', url);

    return this.http.get<any>(url, { params }).pipe(
      tap((res) =>
        console.log('✅ [STATS] getHourlyDistribution response:', res),
      ),
      map((response) => {
        const data = response?.success ? response.data : response;
        if (!data?.hours) return [];
        return data.hours.map((h: any) => ({
          hour: h.hour,
          count: h.count,
          revenue: h.revenue || 0,
        }));
      }),
    );
  }

  /**
   * GET /statistics/appointments/by-day
   */
  getWeeklyDistribution(
    customerId: number,
    siteId: number,
  ): Observable<WeeklyDistribution[]> {
    const filters = this.filterService.getCurrentFilters();
    const params = this.buildDateParams(filters);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/appointments/by-day`;

    console.log('📊 [STATS] getWeeklyDistribution:', url);

    return this.http.get<any>(url, { params }).pipe(
      tap((res) =>
        console.log('✅ [STATS] getWeeklyDistribution response:', res),
      ),
      map((response) => {
        const data = response?.success ? response.data : response;
        if (!data?.days) return [];
        return data.days.map((d: any) => ({
          day: d.dayNumber,
          dayName: d.dayName,
          count: d.count,
          revenue: d.revenue || 0,
        }));
      }),
    );
  }

  /**
   * Wrapper for getHourlyDistribution
   */
  getAppointmentsByHour(
    customerId: number,
    siteId: number,
  ): Observable<HourlyData[]> {
    return this.getHourlyDistribution(customerId, siteId).pipe(
      map((data) => data.map((h) => ({ hour: h.hour, count: h.count }))),
    );
  }

  /**
   * Wrapper for getWeeklyDistribution
   */
  getAppointmentsByDay(
    customerId: number,
    siteId: number,
  ): Observable<CategoryData[]> {
    return this.getWeeklyDistribution(customerId, siteId).pipe(
      map((data) =>
        data.map((d, index) => ({
          id: d.day || index + 1,
          name: d.dayName,
          value: d.revenue,
          count: d.count,
          percentage: 0,
        })),
      ),
    );
  }

  /**
   * GET /statistics/appointments/heatmap
   */
  getAppointmentsHeatmap(
    customerId: number,
    siteId: number,
  ): Observable<AppointmentsHeatmapData | null> {
    const filters = this.filterService.getCurrentFilters();
    const params = this.buildDateParams(filters);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/appointments/heatmap`;

    console.log('📊 [STATS] getAppointmentsHeatmap:', url);

    return this.http.get<any>(url, { params }).pipe(
      tap((res) =>
        console.log('✅ [STATS] getAppointmentsHeatmap response:', res),
      ),
      map((response) => {
        const data = response?.success ? response.data : response;
        if (!data) return null;
        return {
          days: data.days || [],
          hours: data.hours || [],
          matrix: data.matrix || [],
          peakSlot: data.peakSlot || {
            dayNumber: 1,
            dayName: 'Lunes',
            hour: 8,
            count: 0,
          },
          total: data.total || 0,
        } as AppointmentsHeatmapData;
      }),
    );
  }

  /**
   * GET /statistics/appointments/by-status
   */
  getAppointmentsByStatus(
    customerId: number,
    siteId: number,
  ): Observable<CategoryData[]> {
    const filters = this.filterService.getCurrentFilters();
    const params = this.buildDateParams(filters);
    const url = `${this.apiUrl}/customers/${customerId}/sites/${siteId}/statistics/appointments/by-status`;

    console.log('📊 [STATS] getAppointmentsByStatus:', url);

    return this.http.get<any>(url, { params }).pipe(
      tap((res) =>
        console.log('✅ [STATS] getAppointmentsByStatus response:', res),
      ),
      map((response) => {
        const data = response?.success ? response.data : response;
        if (!data?.statuses) return [];
        return data.statuses.map((s: any, index: number) => ({
          id: index + 1,
          name: s.name,
          value: 0,
          count: s.count,
          percentage: s.percentage,
          color: s.color,
        }));
      }),
    );
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private extractRevenue(currencies: any): number {
    // Si es un objeto con monedas, extraer EUR
    if (
      currencies &&
      typeof currencies === 'object' &&
      !Array.isArray(currencies)
    ) {
      return parseFloat(currencies.EUR || currencies.eur || '0');
    }

    // Fallback: si es array (formato antiguo)
    if (Array.isArray(currencies) && currencies.length > 0) {
      const first = currencies[0];
      return parseFloat(first?.total || first?.value || '0');
    }

    return 0;
  }

  private toKpiValue(metric: any): KpiValue {
    if (metric && typeof metric === 'object') {
      const parsedValue = Number(metric.value ?? 0);
      const parsedPreviousValue =
        metric.previousValue === null || metric.previousValue === undefined
          ? null
          : Number(metric.previousValue);
      const parsedTrend =
        metric.trend === null || metric.trend === undefined
          ? null
          : Number(metric.trend);

      return {
        value: isNaN(parsedValue) ? 0 : parsedValue,
        previousValue:
          parsedPreviousValue === null || isNaN(parsedPreviousValue)
            ? null
            : parsedPreviousValue,
        trend: parsedTrend === null || isNaN(parsedTrend) ? null : parsedTrend,
      };
    }

    const fallbackValue = Number(metric ?? 0);
    return {
      value: isNaN(fallbackValue) ? 0 : fallbackValue,
      previousValue: null,
      trend: null,
    };
  }

  private buildDateParams(filters: StatsFiltersWithComparison): HttpParams {
    let params = new HttpParams();

    // Enviar fechas como YYYY-MM-DD usando startDate y endDate
    if (filters.dateRange?.start) {
      const startDate = moment(filters.dateRange.start).format('YYYY-MM-DD');
      params = params.set(
        'startDate',
        startDate,
      );
      params = params.set('dateFrom', startDate);
    }
    if (filters.dateRange?.end) {
      const endDate = moment(filters.dateRange.end).format('YYYY-MM-DD');
      params = params.set(
        'endDate',
        endDate,
      );
      params = params.set('dateTo', endDate);
    }

    console.log('📅 [PARAMS] Final params:', params.toString());

    return params;
  }
}
